<template>
  <div
    class="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex justify-center items-start py-4 overflow-y-auto"
    @click.self="close"
  >
    <div
      class="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl w-full max-w-2xl mx-4 p-8 transform transition-all duration-300 ease-in-out border border-wood-200 my-auto"
      :class="showContent ? 'scale-100 opacity-100' : 'scale-95 opacity-0'"
    >
      <h2 class="text-2xl font-bold text-wood-800 mb-6">
        {{ isEditing ? '編輯貼文' : '發表新貼文' }}
      </h2>

      <form @submit.prevent="submitPost">
        <div class="space-y-6">
          <!-- 武場選擇 -->
          <div v-if="!isEditing">
            <label for="club" class="block text-sm font-medium text-wood-700 mb-2">
              選擇發佈位置
            </label>
            <select
              id="club"
              v-model="postData.club_id"
              class="w-full px-4 py-3 rounded-xl border border-wood-300 focus:border-wood-500 focus:outline-none bg-white/90 backdrop-blur-sm text-wood-800 shadow-sm"
            >

              <option v-for="club in clubs" :key="club.id" :value="club.id">
                {{ club.name }}
              </option>
            </select>
          </div>

          <!-- 貼文內容 -->
          <div>
            <label for="content" class="block text-sm font-medium text-wood-700 mb-2">
              內容
            </label>
            <textarea
              id="content"
              v-model="postData.long"
              rows="8"
              placeholder="分享你的太極心得、影片或任何想法..."
              class="w-full px-4 py-3 rounded-xl border border-wood-300 focus:border-wood-500 focus:outline-none bg-white/90 backdrop-blur-sm text-wood-800 placeholder-wood-500 shadow-sm resize-none"
              required
            ></textarea>
          </div>

          <!-- 檔案上傳 -->
          <div>
            <label for="attachments" class="block text-sm font-medium text-wood-700 mb-2">
              附加圖片
            </label>
            <input
              type="file"
              id="attachments"
              multiple
              accept="image/*"
              @change="handleFileChange"
              class="w-full text-sm text-wood-700 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border file:border-wood-300 file:text-sm file:font-semibold file:bg-white/90 file:backdrop-blur-sm file:text-wood-800 file:shadow-sm hover:file:bg-white hover:file:border-wood-400 file:transition-all file:duration-200"
            />
          </div>

          <!-- 進階模式切換 -->
          <div class="border-t border-wood-200 pt-4">
            <button
              type="button"
              @click="toggleAdvancedMode"
              class="flex items-center justify-center w-full py-3 px-4 rounded-xl border border-wood-300 bg-white/90 backdrop-blur-sm text-wood-700 hover:bg-white hover:border-wood-400 transition-all duration-200 shadow-sm"
            >
              <i :class="['mr-2 transition-transform duration-200', isAdvancedMode ? 'fas fa-chevron-up' : 'fas fa-chevron-down']"></i>
              {{ isAdvancedMode ? '收合進階選項' : '展開進階選項' }}
            </button>
          </div>

          <!-- 進階模式內容 -->
          <div v-if="isAdvancedMode" class="space-y-6 bg-wood-50/50 rounded-xl p-6 border border-wood-200">
            <!-- 精選文章開關 - 僅管理員可見 -->
            <div v-if="userStore.isAdmin" class="border-b border-wood-200 pb-4 mb-4">
              <div class="flex items-center justify-between">
                <div>
                  <label class="block text-sm font-medium text-wood-700 mb-1">
                    精選文章
                  </label>
                  <p class="text-xs text-wood-600">
                    標記為精選的文章會在特別區域顯示
                  </p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="postData.featured"
                    class="sr-only peer"
                  />
                  <div class="w-11 h-6 bg-wood-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-wood-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
            </div>

            <!-- 建議標籤 -->
            <div>
              <label class="block text-sm font-medium text-wood-700 mb-3">
                建議標籤
              </label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="tag in suggestedTags"
                  :key="tag"
                  type="button"
                  @click="toggleTag(tag)"
                  :class="[
                    'px-3 py-1 text-sm rounded-full border transition-all duration-200',
                    selectedTags.includes(tag)
                      ? 'bg-orange-900 text-white border-wood-500 shadow-md'
                      : 'bg-white/90 text-wood-700 border-wood-300 hover:bg-wood-100 hover:border-wood-400'
                  ]"
                >
                  #{{ tag }}
                </button>
              </div>
            </div>

            <!-- 置頂與私密選項 -->
            <div class="space-y-4 border-t border-wood-200 pt-4">
              <!-- 置頂選項 -->
              <div v-if="canPin" class="flex items-center justify-between">
                <div>
                  <label class="block text-sm font-medium text-wood-700 mb-1">
                    置頂貼文
                  </label>
                  <p class="text-xs text-wood-600">
                    {{ pinPermissionText }}
                  </p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="postData.pinned"
                    class="sr-only peer"
                  />
                  <div class="w-11 h-6 bg-wood-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-wood-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
              </div>

              <!-- 私密選項 -->
              <div v-if="showPrivateOption" class="flex items-center justify-between">
                <div>
                  <label class="block text-sm font-medium text-wood-700 mb-1">
                    私密貼文
                  </label>
                  <p class="text-xs text-wood-600">
                    僅同武場成員可查看
                  </p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="postData.isPrivate"
                    class="sr-only peer"
                  />
                  <div class="w-11 h-6 bg-wood-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-wood-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                </label>
              </div>
            </div>

            <!-- 進階欄位 - 小紅書影片資訊（僅管理員可見） -->
            <div v-if="userStore.isAdmin" class="space-y-4">
              <h4 class="text-sm font-medium text-wood-700 border-b border-wood-200 pb-2">
                小紅書影片資訊（選填）
              </h4>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label for="title" class="block text-sm font-medium text-wood-700 mb-1">
                    文章標題
                  </label>
                  <input
                    id="title"
                    v-model="postData.title"
                    type="text"
                    class="w-full px-3 py-2 rounded-lg border border-wood-300 focus:border-wood-500 focus:outline-none bg-white/90 backdrop-blur-sm text-wood-800 text-sm"
                    placeholder="影片標題"
                  />
                </div>

                <div>
                  <label for="authur" class="block text-sm font-medium text-wood-700 mb-1">
                    文章原作
                  </label>
                  <input
                    id="authur"
                    v-model="postData.authur"
                    type="text"
                    class="w-full px-3 py-2 rounded-lg border border-wood-300 focus:border-wood-500 focus:outline-none bg-white/90 backdrop-blur-sm text-wood-800 text-sm"
                    placeholder="原作者名稱"
                  />
                </div>

                <div>
                  <label for="source" class="block text-sm font-medium text-wood-700 mb-1">
                    媒體來源
                  </label>
                  <select
                    id="source"
                    v-model="postData.source"
                    class="w-full px-3 py-2 rounded-lg border border-wood-300 focus:border-wood-500 focus:outline-none bg-white/90 backdrop-blur-sm text-wood-800 text-sm"
                  >
                    <option value="">請選擇</option>
                    <option value="redbook">小紅書</option>
                    <option value="youtube">YouTube</option>
                  </select>
                </div>

                <div>
                  <label for="media" class="block text-sm font-medium text-wood-700 mb-1">
                    媒體類型
                  </label>
                  <select
                    id="media"
                    v-model="postData.media"
                    class="w-full px-3 py-2 rounded-lg border border-wood-300 focus:border-wood-500 focus:outline-none bg-white/90 backdrop-blur-sm text-wood-800 text-sm"
                  >
                    <option value="">請選擇</option>
                    <option value="video">影片</option>
                    <option value="image">圖片</option>
                  </select>
                </div>
              </div>

              <div>
                <label for="video" class="block text-sm font-medium text-wood-700 mb-1">
                  影片網址
                </label>
                <input
                  id="video"
                  v-model="postData.video"
                  type="url"
                  class="w-full px-3 py-2 rounded-lg border border-wood-300 focus:border-wood-500 focus:outline-none bg-white/90 backdrop-blur-sm text-wood-800 text-sm"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label for="preview" class="block text-sm font-medium text-wood-700 mb-1">
                  預覽圖片
                </label>
                <input
                  type="file"
                  id="preview"
                  accept="image/*"
                  @change="handlePreviewChange"
                  class="w-full text-sm text-wood-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border file:border-wood-300 file:text-sm file:font-semibold file:bg-white/90 file:backdrop-blur-sm file:text-wood-800 file:shadow-sm hover:file:bg-white hover:file:border-wood-400 file:transition-all file:duration-200"
                />
              </div>
            </div>
          </div>

          <!-- 預覽圖片 -->
          <div v-if="existingImages.length > 0 || previewUrls.length > 0" class="space-y-4">
            <h4 class="text-sm font-medium text-wood-700">圖片預覽</h4>
            <div class="grid grid-cols-3 gap-4">
              <!-- 現有圖片 -->
              <div v-for="(image, index) in existingImages" :key="`existing-${index}`" class="relative">
                <img :src="image.url" class="w-full h-32 object-cover rounded-lg" />
                <button
                  type="button"
                  @click="removeImage(index, true)"
                  class="absolute top-1 right-1 bg-red-500 bg-opacity-70 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                  title="移除圖片"
                >
                  &times;
                </button>
                <div class="absolute bottom-1 left-1 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                  現有
                </div>
              </div>
              <!-- 新上傳的圖片 -->
              <div v-for="(url, index) in previewUrls" :key="`new-${index}`" class="relative">
                <img :src="url" class="w-full h-32 object-cover rounded-lg" />
                <button
                  type="button"
                  @click="removeImage(existingImages.length + index, false)"
                  class="absolute top-1 right-1 bg-red-500 bg-opacity-70 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                  title="移除圖片"
                >
                  &times;
                </button>
                <div class="absolute bottom-1 left-1 bg-green-500 text-white text-xs px-2 py-1 rounded">
                  新增
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 操作按鈕 -->
        <div class="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            @click="close"
            class="px-6 py-3 rounded-xl border border-wood-300 bg-white/90 backdrop-blur-sm text-wood-700 hover:bg-white hover:border-wood-400 transition-all duration-200 shadow-sm"
          >
            取消
          </button>
          <button
            type="submit"
            :disabled="isSubmitting"
            class="px-6 py-3 rounded-xl border border-wood-300 bg-white/90 backdrop-blur-sm text-wood-700 hover:bg-white hover:border-wood-400 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i v-if="isSubmitting" class="fas fa-spinner animate-spin mr-2"></i>
            {{ isSubmitting ? '發佈中...' : (isEditing ? '更新貼文' : '張貼文章') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import { usePostsStore } from '../stores/posts.js';
import { useUserStore } from '../stores/user.js';

const props = defineProps({
  post: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'post-created', 'post-updated']);

const postsStore = usePostsStore();
const userStore = useUserStore();
const showContent = ref(false);
const clubs = ref([]);
const isSubmitting = ref(false);
const previewUrls = ref([]);
const attachedFiles = ref([]);
const existingImages = ref([]); // 編輯時的現有圖片
const imagesToRemove = ref([]); // 要移除的圖片ID

// 進階模式狀態
const isAdvancedMode = ref(false);
const selectedTags = ref([]);
const previewFile = ref(null);

// 建議標籤列表
const suggestedTags = [
  '拳經拳理', '歷史考據', '往談軼事', '精采影片', '活動資訊',
  '楊家老架', '習拳感悟', '楊氏太極劍', '楊氏太極刀', '楊氏太極槍',
  '推手', '街頭實戰', '健康養生', '陳氏太極拳', '歷史名家'
];

const postData = ref({
  club_id: 0,
  long: '',
  attachments: [],
  // 進階欄位
  title: '',
  authur: '',
  source: '',
  media: '',
  video: '',
  preview: null,
  featured: false,
  // 新增置頂和私密欄位
  pinned: false,
  isPrivate: false,
});

const isEditing = computed(() => !!props.post);

// 權限檢查計算屬性
const canPin = computed(() => {
  // 系統管理員可以置頂任何貼文
  if (userStore.isAdmin) return true;

  // 武場管理員可以置頂武場內貼文
  if (postData.value.club_id > 0) {
    // 這裡需要檢查是否為武場管理員，暫時先允許
    // 後續可以加入武場管理員檢查邏輯
    return false; // 目前先只允許系統管理員置頂
  }

  return false;
});

const pinPermissionText = computed(() => {
  if (userStore.isAdmin) {
    return postData.value.club_id > 0 ? '在武場內置頂' : '在首頁置頂';
  }
  return '在武場內置頂';
});

const showPrivateOption = computed(() => {
  // 只有發佈到武場的貼文才能設為私密
  return postData.value.club_id > 0;
});

// HTML 轉純文字函數 - 最終版，完全保持使用者的換行意圖
const htmlToPlainText = (html) => {
  if (!html) return '';

  // 特殊處理：先檢查是否是純 markdown 產生的 HTML
  // 如果是的話，嘗試保持更多的原始結構

  // 建立一個臨時的 div 元素來解析 HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // 精確地將各種 HTML 元素轉換為換行符，並保持連續換行
  let processedHtml = html
    // 先處理連續的空段落標籤（markdown 產生的）
    .replace(/<p><\/p>/gi, '\n')     // 空段落轉換行
    .replace(/<p>\s*<\/p>/gi, '\n')  // 只有空白的段落

    // 處理一般元素
    .replace(/<br\s*\/?>/gi, '\n')   // <br> 轉換行
    .replace(/<\/p>/gi, '\n')        // </p> 轉換行
    .replace(/<p[^>]*>/gi, '')       // 移除 <p> 開始標籤
    .replace(/<\/div>/gi, '\n')      // </div> 轉換行
    .replace(/<div[^>]*>/gi, '')     // 移除 <div> 開始標籤
    .replace(/<\/h[1-6]>/gi, '\n')   // 標題結尾加換行
    .replace(/<\/li>/gi, '\n')       // 列表項目結尾加換行
    .replace(/<\/blockquote>/gi, '\n'); // 引用結尾加換行

  // 更新 tempDiv 內容來取得純文字
  tempDiv.innerHTML = processedHtml;
  const plainText = tempDiv.textContent || tempDiv.innerText || '';

  // 只清理纁端的空白，保持中間所有的換行（包括連續的）
  return plainText.trim();
};

// 監聽傳入的 post 物件，當它變化時更新表單資料
watch(() => props.post, (newPost) => {
  if (newPost) {
    // 將 HTML 內容轉換回純文字，保持所有使用者的換行意圖
    postData.value.long = htmlToPlainText(newPost.long);

    // 載入進階欄位
    postData.value.title = newPost.title || '';
    postData.value.authur = newPost.authur || '';
    postData.value.source = newPost.source || '';
    postData.value.media = newPost.media || '';
    postData.value.video = newPost.video || '';
    postData.value.featured = newPost.featured || false;

    // 載入置頂和私密欄位
    postData.value.pinned = newPost.pinned || false;
    postData.value.isPrivate = newPost.is_private || false;

    // 載入現有圖片
    if (newPost.images && newPost.images.length > 0) {
      existingImages.value = newPost.images.map((url, index) => ({
        id: index, // 使用實際的索引
        url: url,
        isExisting: true
      }));
    } else {
      existingImages.value = [];
    }

    // 解析現有的 hashtag
    const existingTags = [];
    const hashtagRegex = /#(拳經拳理|歷史考據|往談軼事|精采影片|活動資訊|楊家老架|習拳感悟|楊氏太極劍|楊氏太極刀|楊氏太極槍|推手|街頭實戰|健康養生|陳氏太極拳|生活閒聊)/g;
    let match;
    while ((match = hashtagRegex.exec(postData.value.long)) !== null) {
      if (!existingTags.includes(match[1])) {
        existingTags.push(match[1]);
      }
    }
    selectedTags.value = existingTags;

    // 重設其他狀態
    previewUrls.value = [];
    attachedFiles.value = [];
    imagesToRemove.value = [];
    isAdvancedMode.value = existingTags.length > 0 || postData.value.title || postData.value.video || postData.value.featured; // 如果有進階內容則自動展開
    previewFile.value = null;
  } else {
    // 新建模式，重設表單
    postData.value = {
      club_id: 0,
      long: '',
      attachments: [],
      title: '',
      authur: '',
      source: '',
      media: '',
      video: '',
      preview: null,
      featured: false,
      pinned: false,
      isPrivate: false,
    };
    existingImages.value = [];
    previewUrls.value = [];
    attachedFiles.value = [];
    imagesToRemove.value = [];
    selectedTags.value = [];
    isAdvancedMode.value = false;
    previewFile.value = null;
  }
}, { immediate: true });


const fetchUserClubs = async () => {
  try {
    const response = await axios.get('/api/v1/users/clubs');
    clubs.value = response.data;
  } catch (error) {
    console.error('無法獲取武場列表:', error);
  }
};

const handleFileChange = (event) => {
  const files = Array.from(event.target.files);
  attachedFiles.value.push(...files);

  files.forEach(file => {
    const reader = new FileReader();
    reader.onload = (e) => {
      previewUrls.value.push(e.target.result);
    };
    reader.readAsDataURL(file);
  });
};

const removeImage = (index, isExisting = false) => {
  if (isExisting) {
    // 移除現有圖片
    const imageToRemove = existingImages.value[index];
    // 傳送格式為 "existing_N" 給後端，符合後端處理邏輯
    imagesToRemove.value.push(`existing_${imageToRemove.id}`);
    existingImages.value.splice(index, 1);
  } else {
    // 移除新上傳的圖片
    const adjustedIndex = index - existingImages.value.length;
    attachedFiles.value.splice(adjustedIndex, 1);
    previewUrls.value.splice(adjustedIndex, 1);
  }
};

// 進階模式相關函數
const toggleAdvancedMode = () => {
  isAdvancedMode.value = !isAdvancedMode.value;
};

const toggleTag = (tag) => {
  const currentContent = postData.value.long;
  const hashtag = `#${tag}`;

  if (selectedTags.value.includes(tag)) {
    // 移除標籤
    selectedTags.value = selectedTags.value.filter(t => t !== tag);
    // 從文字內容中移除 hashtag
    postData.value.long = currentContent.replace(new RegExp(`\\s*${hashtag}\\s*`, 'g'), ' ').trim();
  } else {
    // 添加標籤
    selectedTags.value.push(tag);
    // 在文字內容末尾添加 hashtag
    postData.value.long = currentContent.trim() + (currentContent.trim() ? ' ' : '') + hashtag;
  }
};

const handlePreviewChange = (event) => {
  const file = event.target.files[0];
  if (file) {
    previewFile.value = file;
  }
};

const submitPost = async () => {
  isSubmitting.value = true;

  const formData = new FormData();
  formData.append('post[long]', postData.value.long);

  if (!isEditing.value) {
    formData.append('post[club_id]', postData.value.club_id);
  }

  // 添加進階欄位
  if (postData.value.title) {
    formData.append('post[title]', postData.value.title);
  }
  if (postData.value.authur) {
    formData.append('post[authur]', postData.value.authur);
  }
  if (postData.value.source) {
    formData.append('post[source]', postData.value.source);
  }
  if (postData.value.media) {
    formData.append('post[media]', postData.value.media);
  }
  if (postData.value.video) {
    formData.append('post[video]', postData.value.video);
  }

  // 添加預覽圖片
  if (previewFile.value) {
    formData.append('post[preview]', previewFile.value);
  }

  // 添加精選文章狀態
  formData.append('post[featured]', postData.value.featured);

  // 添加置頂和私密狀態
  formData.append('post[pinned]', postData.value.pinned);
  formData.append('post[is_private]', postData.value.isPrivate);

  // 添加新圖片
  attachedFiles.value.forEach(file => {
    formData.append('post[attachments][]', file);
  });

  // 編輯模式下需要傳送要移除的圖片資訊
  if (isEditing.value && imagesToRemove.value.length > 0) {
    imagesToRemove.value.forEach(imageId => {
      formData.append('post[remove_images][]', imageId);
    });
  }

  // 從 Rails session cookie 中獲取 CSRF token
  const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

  try {
    let response;
    if (isEditing.value) {
      // 更新貼文
      response = await axios.patch(`/posts/${props.post.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
      });
      emit('post-updated', response.data);
    } else {
      // 建立新貼文
      response = await axios.post('/posts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
      });
      emit('post-created', response.data);
    }
    close();
  } catch (error) {
    console.error('貼文送出失敗:', error.response?.data || error);
    // 可以在此處加入錯誤提示
  } finally {
    isSubmitting.value = false;
  }
};

const close = () => {
  emit('close');
};

onMounted(() => {
  // 讓 Modal 有個淡入效果
  setTimeout(() => {
    showContent.value = true;
  }, 50);

  if (!isEditing.value) {
    fetchUserClubs();
  }
});
</script>
